<template>
  <div :id="id" class="tw:flex tw:flex-col" :style="height ? { height } : undefined">
    <div role="tablist" class="tabs-play-v2__tablist tw:flex tw:overflow-x-auto tw:-mx-1 tw:px-1">
      <button
        v-for="tab in visibleTabs"
        :key="tab.id"
        type="button"
        role="tab"
        :data-testid="tab.id"
        :aria-selected="modelValue === tab.id"
        :aria-controls="`tabpanel-${tab.id}`"
        :disabled="!isTabEnabled(tab.id)"
        :class="[
          'tw:flex tw:items-center tw:px-4 tw:py-2 tw:font-bold tw:whitespace-nowrap tw:cursor-pointer tw:bg-transparent tw:outline-none tw:rounded-none tw:transition-all tw:duration-150 tw:border-x-0 tw:border-t-0 tw:border-b-2 tw:border-solid',
          modelValue === tab.id && !skeleton
            ? 'tw:opacity-100 tw:border-b-(--maincolor)'
            : 'tw:border-b-transparent',
          modelValue !== tab.id && !skeleton && isTabEnabled(tab.id) && 'tw:opacity-50 hover:tw:opacity-75',
          !isTabEnabled(tab.id) && !skeleton && 'tw:opacity-40 tw:cursor-not-allowed',
          skeleton && 'skeleton-play',
        ]"
        @click="handleTabClick(tab.id)"
      >
        <IconGoogle
          v-if="tab.icon && typeof tab.icon === 'string'"
          :name="tab.icon"
          size="1rem"
          class="tw:mr-2"
        />
        <component
          :is="tab.icon"
          v-else-if="tab.icon && typeof tab.icon === 'object'"
          v-bind="tab.iconProps ?? {}"
          class="tw:mr-2"
        />
        <TextPlay tag="span" weight="bold" :size="textSize" :text="tab.label" />
      </button>
    </div>

    <div
      :id="`tabpanel-${modelValue}`"
      :key="modelValue"
      role="tabpanel"
      :class="['tw:flex tw:flex-col tw:flex-1 tw:mt-4', !noContentOverflow && 'tw:overflow-y-auto!']"
    >
      <slot :name="modelValue" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, type Component } from 'vue'
import IconGoogle from '../../atoms/IconGoogle/IconGoogle.vue'
import TextPlay from '../../atoms/TextPlay/TextPlay.vue'

interface Tab {
  id: string
  icon?: string | Component
  label: string
  iconProps?: Record<string, unknown>
}

const props = withDefaults(defineProps<{
  id?: string
  tabs?: Tab[]
  modelValue?: string
  height?: string
  disabledTabs?: Array<string | number>
  hiddenTabs?: string[]
  skeleton?: boolean
  noContentOverflow?: boolean
  textSize?: 'xxs' | 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | '8xl' | '9xl'
}>(), {
  id: () => `tabs-play-${Math.random().toString(36).substring(2, 15)}`,
  tabs: () => [],
  height: undefined,
  textSize: 'base',
  disabledTabs: () => [],
  hiddenTabs: () => [],
  modelValue: undefined,
  skeleton: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [id: string | number]
}>()

const visibleTabs = computed(() =>
  props.tabs.filter(Boolean).filter(tab => !props.hiddenTabs.includes(tab.id))
)

function isTabEnabled(id: string | number): boolean {
  return !props.disabledTabs.includes(id)
}

function handleTabClick(id: string | number) {
  if (!isTabEnabled(id)) return
  emit('update:modelValue', id)
}
</script>

<style lang="scss" scoped>
.tabs-play-v2__tablist {
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
